import { Box, Step, StepIconProps, StepLabel, Stepper, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import { JSX } from 'react';
import { ReportFullStepList, ReportSystemSteps } from '../types';

interface StepHeaderProps {
  sectionName: ReportFullStepList;
  availableSections: ReportFullStepList[];
}

const CustomStepIcon = ({ active, completed }: StepIconProps): JSX.Element => {
  if (completed) {
    return (
      <Box
        width={20}
        height={20}
        bgcolor="primary.main"
        className="rounded-full flex items-center justify-center text-white relative z-10"
      >
        <Icon icon="icon-park-outline:check-small" />
      </Box>
    );
  }
  if (active) {
    return (
      <Box
        width={20}
        height={20}
        bgcolor="secondary.main"
        className="rounded-full flex items-center justify-center relative z-10"
      >
        <Box width={6} height={6} className="rounded-full bg-white" />
      </Box>
    );
  }
  return (
    <Box width={20} height={20} className="flex items-center justify-center relative z-10">
      <Box width={6} height={6} bgcolor="primary.main" className="rounded-full" />
    </Box>
  );
};

const systemSections = Object.values(ReportSystemSteps);

/**
 * Render report header with step indicator
 */
const StepHeader = ({ sectionName, availableSections }: StepHeaderProps): JSX.Element => {
  // Exclude system sections from the step indicator (like "Choose sections", "select FNAs", etc)
  const stepperSteps = availableSections.filter(
    (sectionName) => !systemSections.includes(sectionName as ReportSystemSteps),
  );

  const currentIsSystemSection = systemSections.includes(sectionName as ReportSystemSteps);

  return (
    <Box className="space-y-1 my-8">
      <Typography color="primary.main">
        Let`s go through the report builder and customize it before you finalise it to send to your
        client. We will step through it section by section.
      </Typography>
      {!currentIsSystemSection && (
        <Box className="pt-6 overflow-x-auto overflow-y-hidden">
          <Stepper
            alternativeLabel
            sx={{
              '& .MuiStepConnector-root': {
                top: 8.5,
              },
              '& .MuiStepConnector-root .MuiStepConnector-line': {
                borderWidth: 1.5,
                borderColor: '#DDE1E8',
              },
              '& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line, & .MuiStepConnector-root.Mui-active .MuiStepConnector-line':
                {
                  borderColor: 'primary.main',
                },
              '& .MuiStepLabel-label.Mui-active': {
                color: 'secondary.main',
                fontWeight: 600,
              },
              '& .MuiStepLabel-label': {
                marginTop: 1,
                lineHeight: 1,
              },
            }}
          >
            {stepperSteps.map((availableSectionName) => (
              <Step
                key={availableSectionName}
                active={availableSectionName === sectionName}
                completed={false}
              >
                <StepLabel StepIconComponent={CustomStepIcon}>{availableSectionName}</StepLabel>
              </Step>
            ))}
          </Stepper>
        </Box>
      )}
    </Box>
  );
};

export default StepHeader;
